<template>
<page-wrapper>
  <v-row>
    <v-col v-for="branch in branches" :key="branch.value" cols="12" lg="6">
      <v-card flat outlined>
        <v-card-title>
          {{ branch.text }}
          <v-spacer></v-spacer>
          <v-btn small depressed color="primary" @click="openAdd(branch.value)">New rate</v-btn>
        </v-card-title>
        <v-simple-table>
          <thead>
            <tr>
              <th>Role</th>
              <th class="rate-col">Hourly Rate</th>
            </tr>
          </thead>
          <tbody>
            <template v-for="group in groupedFor(branch.value)">
              <tr :key="group.key" class="group-row">
                <td colspan="2">
                  <div class="period">
                    {{ formatDate(group.date) }}
                    {{ group.end ? 'to ' + formatDate(group.end) : 'onwards' }}
                  </div>
                </td>
              </tr>
              <tr v-for="entry in group.entries" :key="entry.id">
                <td class="pl-8">{{ formatRole(entry.role_name) }}</td>
                <td class="rate-col">£{{ entry.hourly_rate }}</td>
              </tr>
            </template>
            <tr v-if="!groupedFor(branch.value).length">
              <td colspan="2" class="grey--text">No rates yet</td>
            </tr>
          </tbody>
        </v-simple-table>
      </v-card>
    </v-col>
  </v-row>

  <v-dialog v-model="addDialog" max-width="360px">
    <v-card>
      <v-card-title>New rate</v-card-title>
      <v-card-text>
        <v-select v-model="newRate.role_name" :items="roleOptions" label="Role"></v-select>
        <date-picker v-model="newRate.date" label="Start date"></date-picker>
        <v-text-field v-model="newRate.rate" type="number" prefix="£" label="Rate (£/hr)"
                      @keyup.enter="addRate"></v-text-field>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn text @click="addDialog = false">Cancel</v-btn>
        <v-btn text color="primary" :disabled="!canAdd" @click="addRate">Add</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</page-wrapper>
</template>

<script>
import * as api from '../api'
import { branchOptions, userOptions } from '../data'
import PageWrapper from '../components/ui/PageWrapper'
import DatePicker from '../components/ui/DatePicker'
import dayjs from 'dayjs'

export default {
  name: 'ChargeOutRates',
  components: { PageWrapper, DatePicker },
  data() {
    return {
      branches: branchOptions,
      roles: userOptions.role,
      rates: [],
      addDialog: false,
      newRate: { role_name: '', branch: '', date: '2000-01-01', rate: '' }
    }
  },
  computed: {
    roleOptions() {
      return this.roles.map(role => ({ text: this.formatRole(role), value: role }))
    },
    canAdd() {
      const { role_name, rate } = this.newRate
      return !!role_name && rate !== '' && !Number.isNaN(parseFloat(rate))
    }
  },
  methods: {
    formatRole(role) {
      return role.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
    },
    formatDate(date) {
      return dayjs(date).format('DD/MM/YYYY')
    },
    toDateStr(timestamp) {
      return timestamp ? dayjs(timestamp).format('YYYY-MM-DD') : ''
    },
    groupKey(branch, date) {
      return `${branch}|${date}`
    },
    // Group entries by their start date: each distinct date is a rate revision
    // effective until the next one (the latest runs "onwards").
    groupedFor(branch) {
      const entries = this.rates.filter(r => r.branch === branch)
      const dates = [...new Set(entries.map(e => this.toDateStr(e.timestamp)))].sort()
      return dates.map((date, i) => ({
        key: this.groupKey(branch, date),
        branch,
        date,
        end: dates[i + 1] || null,
        entries: entries
          .filter(e => this.toDateStr(e.timestamp) === date)
          .sort((a, b) => {
            const ia = this.roles.indexOf(a.role_name)
            const ib = this.roles.indexOf(b.role_name)
            return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib) || a.role_name.localeCompare(b.role_name)
          })
      })).reverse()
    },
    openAdd(branch) {
      const date = dayjs().format('YYYY-MM-DD')
      this.newRate = { role_name: '', branch, date, rate: '' }
      this.addDialog = true
    },
    async addRate() {
      const { role_name, branch, date, rate } = this.newRate
      if (!role_name || !date || rate === '' || Number.isNaN(parseFloat(rate))) return
      await api.post('/charged-out', {
        role_name,
        branch,
        hourly_rate: parseFloat(rate),
        timestamp: date
      })
      this.addDialog = false
      await this.refresh()
    },
    async refresh() {
      this.rates = await api.get('/charged-out')
    }
  },
  async created() {
    await this.refresh()
  }
}
</script>

<style scoped lang="scss">
.rate-col {
  width: 160px;
}

.group-row {
  background: #f5f5f5;
}
</style>
